/**
 * The mock dataset behind every page.
 *
 * Shaped exactly like the `/api/v1` responses so `httpApi.ts` can replace `mockApi.ts` without
 * a single page changing. Names, languages, products and prices are real Main Character material;
 * scoring factors come from packs/main-character/scoring.yaml; handoff bases and outcomes come
 * from db/migrations/018_handoff.sql.
 *
 * Author: Danish Khan
 */
import type {
  Contact,
  Conversation,
  Explain,
  Handoff,
  HealthSummary,
  Lead,
  LeadFact,
  Media,
  Message,
  MetricDefinition,
  MetricValue,
  PipelineCard,
  PipelineStage,
  Score,
  ScoreFactor,
  TimelineEntry,
  User,
} from '../lib/types.ts';
import { MOCK_PRODUCTS } from './catalogue.data.ts';
import { THREADS, type SeedMessage } from './threads.ts';

/** One clock for the whole mock so SLA ages and window timers are consistent and always fresh. */
export const NOW = Date.now();
export const ago = (minutes: number): string => new Date(NOW - minutes * 60_000).toISOString();
const mins = (iso: string): number => Math.round((NOW - Date.parse(iso)) / 60_000);

export const TENANT = {
  id: 'main-character',
  name: 'Main Character',
  agentName: 'Maya',
  timezone: 'Asia/Kolkata',
};

export const USERS: User[] = [
  {
    id: 'u-kavisha',
    tenantId: TENANT.id,
    name: 'Kavisha Mehra',
    email: 'kavisha@maincharacterglobal.com',
    role: 'owner',
    active: true,
    lastLoginAt: ago(180),
    createdAt: ago(60 * 24 * 40),
  },
  {
    id: 'u-ritu',
    tenantId: TENANT.id,
    name: 'Ritu Bhandari',
    email: 'ritu@maincharacterglobal.com',
    role: 'manager',
    active: true,
    lastLoginAt: ago(35),
    createdAt: ago(60 * 24 * 38),
  },
  {
    id: 'u-karthik',
    tenantId: TENANT.id,
    name: 'R. Karthik',
    email: 'karthik@maincharacterglobal.com',
    role: 'rep',
    active: true,
    lastLoginAt: ago(12),
    createdAt: ago(60 * 24 * 21),
  },
  {
    id: 'u-sneha',
    tenantId: TENANT.id,
    name: 'Sneha Pillai',
    email: 'sneha@maincharacterglobal.com',
    role: 'rep',
    active: true,
    lastLoginAt: ago(410),
    createdAt: ago(60 * 24 * 19),
  },
  {
    id: 'u-aarav',
    tenantId: TENANT.id,
    name: 'Aarav Menon',
    email: 'aarav@maincharacterglobal.com',
    role: 'rep',
    active: false,
    lastLoginAt: ago(60 * 24 * 26),
    createdAt: ago(60 * 24 * 33),
  },
  {
    id: 'u-danish',
    tenantId: TENANT.id,
    name: 'Danish Khan',
    email: 'ops@example.com',
    role: 'ops',
    active: true,
    lastLoginAt: ago(4),
    createdAt: ago(60 * 24 * 41),
  },
];

// ---------------------------------------------------------------------------------------------
// Contacts and leads
// ---------------------------------------------------------------------------------------------

interface Seed {
  key: string;
  name: string;
  phone: string;
  lang: Contact['lang'];
  city: string | null;
  type: Contact['customerType'];
  stage: string;
  facts: [key: string, value: string, source: LeadFact['source'], quote: string | null][];
  hits: string[];
  /** Evidence for factors that have no ledger fact behind them (price_asked, asked_for_call_or_visit). */
  quotes?: Record<string, string>;
  /**
   * Signals about this seed, used here to pick a plausible `source`. **Not** written to
   * `Lead.tags`: `routes/leads.ts` answers `tags: []` for every lead, because there is no tagging
   * feature and no table behind one. The demo used to show `#marble` `#mumbai` pills the real
   * console can never produce, on the screen the client was shown it on.
   */
  tags: string[];
  optedOut?: boolean;
}

const SEEDS: Seed[] = [
  {
    key: 'anita',
    name: 'अनीता शर्मा',
    phone: '+91 98201 44507',
    lang: 'hi',
    city: 'Mumbai',
    type: 'b2c',
    stage: 'handed_over',
    facts: [
      ['name', 'अनीता शर्मा', 'customer', 'मैं अनीता शर्मा, मुंबई से।'],
      ['city', 'Mumbai', 'customer', 'मैं अनीता शर्मा, मुंबई से।'],
      ['requirement', 'home', 'customer', 'घर के लिए, अपने ड्रॉइंग रूम के लिए'],
      ['space', 'drawing room', 'customer', 'अपने ड्रॉइंग रूम के लिए'],
      ['category', 'side table', 'customer', 'मुझे अपने लिविंग रूम के लिए एक साइड टेबल चाहिए'],
      ['style', 'statement, marble', 'customer', 'कुछ statement type, marble में हो तो अच्छा'],
      ['product_interest', 'Cantilever Marble (KL 1483 A)', 'customer', 'Cantilever वाला बहुत सुंदर है'],
    ],
    hits: ['requirement_known', 'space_known', 'category_named', 'style_known', 'product_named_by_customer', 'asked_for_call_or_visit'],
    quotes: {
      asked_for_call_or_visit: 'हाँ, please कॉल करवा दीजिए। मैं अनीता शर्मा, मुंबई से।',
    },
    tags: ['marble', 'mumbai', 'instagram'],
  },
  {
    key: 'rohit',
    name: 'Rohit Bansal',
    phone: '+91 99103 22841',
    lang: 'en',
    city: 'Jaipur',
    type: 'b2b',
    stage: 'handed_over',
    facts: [
      ['name', 'Rohit Bansal', 'customer', 'Rohit Bansal, Bansal Hospitality.'],
      ['city', 'Jaipur', 'customer', "we're fitting out a 40-key boutique hotel in Jaipur"],
      ['project_scope', '40-key boutique hotel', 'customer', 'a 40-key boutique hotel in Jaipur, opening March'],
      ['piece_count', '80', 'customer', 'One console + one mirror per room'],
      ['timeline', 'March opening', 'customer', 'opening March'],
      ['category', 'console tables, mirrors', 'customer', 'Looking for console tables and mirrors'],
      ['product_interest', 'Arch Console (KL 2194)', 'rep', null],
    ],
    hits: ['requirement_known', 'category_named', 'product_named_by_customer', 'asked_for_call_or_visit', 'b2b_project_scope', 'price_asked'],
    quotes: {
      asked_for_call_or_visit: 'Yes please. Rohit Bansal, Bansal Hospitality. Best on this number after 4pm.',
      price_asked: 'What sort of lead time on 80 pieces?',
    },
    tags: ['project', 'hospitality', 'jaipur'],
  },
  {
    key: 'karthik',
    name: 'R. Karthik',
    phone: '+91 94440 10238',
    lang: 'hinglish',
    city: 'Chennai',
    type: 'b2c',
    stage: 'qualified',
    facts: [
      ['requirement', 'home', 'customer', 'Ghar ke liye. Dining room mein rakhna hai'],
      ['space', 'dining room', 'customer', 'Dining room mein rakhna hai'],
      ['category', 'credenza', 'customer', 'Athena credenza ka price kya hai?'],
      ['budget', '₹1,50,000', 'customer', '1.5 lakh tak theek hai'],
      ['city', 'Chennai', 'customer', 'Delivery Chennai ho jayegi?'],
      ['product_interest', 'Chequered Credenza (KL 2096)', 'customer', 'Chequered zyada practical lag raha'],
    ],
    hits: ['requirement_known', 'space_known', 'category_named', 'budget_stated', 'product_named_by_customer', 'price_asked'],
    quotes: {
      price_asked: 'Athena credenza ka price kya hai?',
    },
    tags: ['credenza', 'chennai'],
  },
  {
    key: 'farah',
    name: 'Farah Qureshi',
    phone: '+91 98337 71190',
    lang: 'en',
    city: 'Hyderabad',
    type: 'b2c',
    stage: 'handed_over',
    facts: [
      ['name', 'Farah Qureshi', 'import', null],
      ['city', 'Hyderabad', 'import', null],
      ['product_interest', 'Casablanca Mirror Frame Mop (KL 250)', 'system', 'The Casablanca mirror arrived yesterday'],
    ],
    hits: ['category_named', 'product_named_by_customer'],
    tags: ['after-sales', 'replacement'],
  },
  {
    key: 'siddharth',
    name: 'Siddharth Rao',
    phone: '+91 90042 88316',
    lang: 'en',
    city: null,
    type: 'unknown',
    stage: 'new',
    facts: [
      ['category', 'console', 'customer', 'what is the price of the arch console'],
      ['product_interest', 'Arch Console (KL 2194)', 'customer', 'the cheaper one'],
    ],
    hits: ['category_named', 'price_asked', 'product_named_by_customer', 'asked_for_call_or_visit'],
    quotes: {
      price_asked: 'what is the price of the arch console',
      asked_for_call_or_visit: 'the cheaper one. can someone call me today',
    },
    tags: [],
  },
  {
    key: 'priyanka',
    name: 'प्रियंका शर्मा',
    phone: '+91 97178 35520',
    lang: 'hi',
    city: 'Delhi',
    type: 'b2c',
    stage: 'engaged',
    facts: [
      ['name', 'प्रियंका शर्मा', 'customer', 'प्रियंका शर्मा. मुझे mirror चाहिए'],
      ['space', 'entryway', 'customer', 'मुझे mirror चाहिए, entryway के लिए'],
      ['category', 'mirror', 'customer', 'मुझे mirror चाहिए'],
      ['product_interest', 'Arabesque Mirror Frame (KL 1155)', 'customer', 'Arabesque पसंद आया'],
    ],
    hits: ['space_known', 'category_named', 'product_named_by_customer'],
    tags: ['mirror', 'delhi'],
  },
  {
    key: 'ayesha',
    name: 'Ayesha Khan',
    phone: '+91 98670 40025',
    lang: 'en',
    city: 'Mumbai',
    type: 'b2c',
    stage: 'engaged',
    facts: [
      ['category', 'console', 'customer', 'Do you make console tables in a smaller size?'],
      ['space', 'hallway', 'customer', 'My hallway is narrow'],
    ],
    hits: ['category_named', 'space_known'],
    tags: ['custom-size', 'instagram'],
  },
  {
    key: 'mehul',
    name: 'Mehul Doshi',
    phone: '+91 93222 60714',
    lang: 'hinglish',
    city: 'Ahmedabad',
    type: 'b2c',
    stage: 'engaged',
    facts: [
      ['space', 'living room', 'customer', 'Living room. Bhaav thoda kam ho sakta hai kya?'],
      ['category', 'cocktail table', 'customer', 'Do you have anything in agate or onyx?'],
      ['product_interest', 'Agate Top Cocktail Table (KL 2105)', 'system', null],
    ],
    hits: ['space_known', 'category_named', 'price_asked'],
    quotes: {
      price_asked: 'Bhaav thoda kam ho sakta hai kya?',
    },
    tags: ['price-sensitive'],
  },
  {
    key: 'tanvi',
    name: 'Tanvi Deshpande',
    phone: '+91 90283 17744',
    lang: 'en',
    city: 'Pune',
    type: 'b2c',
    stage: 'engaged',
    facts: [
      ['city', 'Pune', 'customer', 'Hi, do you ship to Pune?'],
      ['category', 'coffee table', 'customer', 'A coffee table, something in wood and brass'],
      ['style', 'wood and brass', 'customer', 'something in wood and brass'],
    ],
    hits: ['category_named', 'style_known'],
    tags: ['pune'],
  },
  {
    key: 'srinivasan',
    name: 'K. Srinivasan',
    phone: '+91 98450 93012',
    lang: 'en',
    city: 'Bengaluru',
    type: 'b2b',
    stage: 'handed_over',
    facts: [
      ['name', 'K. Srinivasan', 'customer', 'K. Srinivasan here.'],
      ['city', 'Bengaluru', 'customer', 'Bengaluru. Simple, marble top preferred.'],
      ['project_scope', 'serviced apartment block', 'customer', 'Need 6 identical side tables for a serviced apartment block.'],
      ['piece_count', '6', 'customer', 'Need 6 identical side tables'],
      ['style', 'simple, marble top', 'customer', 'Simple, marble top preferred.'],
    ],
    hits: ['requirement_known', 'category_named', 'style_known', 'b2b_project_scope', 'asked_for_call_or_visit'],
    quotes: {
      asked_for_call_or_visit: 'Tomorrow is fine.',
    },
    tags: ['project', 'bengaluru'],
  },
  {
    key: 'sumitra',
    name: 'सुमित्रा देवी',
    phone: '+91 94151 20386',
    lang: 'hi',
    city: 'Lucknow',
    type: 'b2c',
    stage: 'qualified',
    facts: [
      ['space', 'बैठक', 'customer', 'बैठक के लिए एक टेबल'],
      ['category', 'side table', 'customer', 'बैठक के लिए एक टेबल'],
      ['budget', '₹28,000', 'customer', '28 हज़ार वाला ठीक है'],
      ['product_interest', 'Bone Inlay Side Table (KL 1335)', 'customer', '28 हज़ार वाला ठीक है'],
    ],
    hits: ['space_known', 'category_named', 'budget_stated', 'product_named_by_customer', 'price_asked'],
    quotes: {
      price_asked: 'भाव बताइए',
    },
    tags: ['lucknow'],
  },
  {
    key: 'zoya',
    name: 'Zoya Merchant',
    phone: '+91 99304 55218',
    lang: 'en',
    city: 'Mumbai',
    type: 'b2c',
    stage: 'qualified',
    facts: [
      ['category', 'bar cabinet', 'customer', 'Do you do bar cabinets?'],
      ['product_interest', 'Aziza 2 Door Cabinet (KL 1723)', 'customer', 'Aziza. What colours?'],
      ['style', 'bone inlay', 'system', null],
    ],
    hits: ['category_named', 'style_known', 'product_named_by_customer'],
    tags: ['cabinet', 'mumbai'],
  },
  {
    key: 'vikram',
    name: 'Vikram Chauhan',
    phone: '+91 98110 76643',
    lang: 'en',
    city: 'Gurugram',
    type: 'b2b',
    stage: 'closed',
    facts: [
      ['name', 'Vikram Chauhan', 'rep', null],
      ['project_scope', 'restaurant', 'customer', 'Restaurant project, 12 tables + entrance console.'],
      ['piece_count', '13', 'customer', '12 tables + entrance console'],
      ['timeline', '6 weeks', 'customer', 'opening in 6 weeks'],
      ['city', 'Gurugram', 'rep', null],
    ],
    hits: ['requirement_known', 'category_named', 'b2b_project_scope', 'asked_for_call_or_visit', 'price_asked', 'budget_stated'],
    quotes: {
      price_asked: 'Send the invoice.',
      asked_for_call_or_visit: 'Send the split in writing.',
      budget_stated: 'Received. Going with it. Send the invoice.',
    },
    tags: ['project', 'restaurant', 'phased-delivery'],
  },
  {
    key: 'neha',
    name: 'नेहा गुप्ता',
    phone: '+91 96540 88121',
    lang: 'hi',
    city: 'Jaipur',
    type: 'b2c',
    stage: 'closed',
    facts: [
      ['name', 'नेहा गुप्ता', 'import', null],
      ['category', 'bedside', 'customer', 'क्या आपके पास bone inlay के bedside tables हैं?'],
      ['style', 'bone inlay', 'customer', 'bone inlay के bedside tables'],
    ],
    hits: ['category_named', 'style_known'],
    tags: ['reengaged'],
    optedOut: false,
  },
];

/**
 * packs/main-character/scoring.yaml — points and labels, not invented.
 *
 * `fact` is the ledger key whose quote is the evidence for that factor. The two intent factors
 * have no fact behind them, so their evidence comes from the seed's `quotes` map. A factor whose
 * evidence is some other factor's quote is worse than no evidence at all — it looks like proof.
 */
const FACTOR_CATALOGUE: { key: string; label: string; points: number; fact: string | null }[] = [
  { key: 'requirement_known', label: 'Told us whether it is home or a project', points: 5, fact: 'requirement' },
  { key: 'space_known', label: 'Named the room or space', points: 10, fact: 'space' },
  { key: 'category_named', label: 'Named a category of piece', points: 10, fact: 'category' },
  { key: 'style_known', label: 'Described the look they want', points: 5, fact: 'style' },
  { key: 'budget_stated', label: 'Stated a budget', points: 20, fact: 'budget' },
  { key: 'product_named_by_customer', label: 'Named a specific product themselves', points: 15, fact: 'product_interest' },
  { key: 'price_asked', label: 'Asked what something costs', points: 10, fact: null },
  { key: 'asked_for_call_or_visit', label: 'Asked for a call or a studio visit', points: 30, fact: null },
  { key: 'b2b_project_scope', label: 'Described a B2B project scope', points: 25, fact: 'project_scope' },
];

/** tiers: { hot: 70, warm: 40, cold: 0 } — packs/main-character/scoring.yaml */
function tierFor(score: number): Contact['tier'] {
  return score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
}

function buildScore(seed: Seed): Score {
  const factors: ScoreFactor[] = FACTOR_CATALOGUE.map((f) => {
    const hit = seed.hits.includes(f.key);
    const fromFact = f.fact ? seed.facts.find(([key, , , q]) => key === f.fact && q !== null)?.[3] : undefined;
    const fromIntent = seed.quotes?.[f.key];
    return {
      key: f.key,
      label: f.label,
      points: f.points,
      hit,
      evidence: hit ? (fromIntent ?? fromFact ?? null) : null,
    };
  });
  const value = factors.reduce((sum, f) => sum + (f.hit ? f.points : 0), 0);
  return { value, tier: tierFor(value), version: 'mc-1.0', factors, computedAt: ago(3) };
}

const threadByKey = new Map(THREADS.map((t) => [t.key, t.messages]));
const productById = new Map(MOCK_PRODUCTS.map((p) => [p.id, p]));

function mediaFor(ids: string[] | undefined): Media[] | null {
  if (!ids?.length) return null;
  const out: Media[] = [];
  for (const id of ids) {
    const hero = productById.get(id)?.media[0];
    if (hero) out.push(hero);
  }
  return out.length ? out : null;
}

function buildMessages(key: string): Message[] {
  const seeds = threadByKey.get(key) ?? [];
  const conversationId = `c-${key}`;
  return seeds
    .map((m: SeedMessage, i: number): Message => {
      const at = ago(m.ago);
      return {
        id: `m-${key}-${i + 1}`,
        conversationId,
        direction: m.dir,
        kind: m.kind ?? (m.template ? 'template' : 'text'),
        body: m.body,
        at,
        delivery: m.dir === 'out' ? (m.delivery ?? 'sent') : null,
        failureReason: m.failure ?? null,
        reason: m.dir === 'out' ? (m.reason ?? 'turn') : null,
        template: m.template ?? null,
        media: mediaFor(m.media),
        turnId: m.turn ?? null,
        author: m.author ?? null,
      };
    })
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

export const MESSAGES: Record<string, Message[]> = Object.fromEntries(
  SEEDS.map((s) => [`c-${s.key}`, buildMessages(s.key)]),
);

function timelineFor(seed: Seed, messages: Message[], score: Score): TimelineEntry[] {
  const entries: TimelineEntry[] = messages.map((m) => ({
    id: `tl-${m.id}`,
    at: m.at,
    kind: 'message',
    title:
      m.direction === 'in'
        ? 'Inbound message'
        : m.reason === 'human'
          ? `Human reply — ${m.author ?? 'rep'}`
          : m.kind === 'template'
            ? `Template — ${m.template?.name ?? ''}`
            : 'Maya replied',
    body: m.body.length > 160 ? `${m.body.slice(0, 157)}…` : m.body,
    actor: m.direction === 'in' ? seed.name : (m.author ?? TENANT.agentName),
  }));
  entries.push({
    id: `tl-score-${seed.key}`,
    at: score.computedAt,
    kind: 'score',
    title: `Score ${score.value} (${score.tier})`,
    body: `${score.factors.filter((f) => f.hit).length} of ${score.factors.length} factors hit · scoring ${score.version}`,
    actor: 'system',
  });
  entries.push({
    id: `tl-stage-${seed.key}`,
    at: ago(mins(messages[messages.length - 1]?.at ?? ago(60)) + 5),
    kind: 'stage',
    title: `Stage → ${seed.stage}`,
    body: 'Moved by the scoring pass, not by hand',
    actor: 'system',
  });
  return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

export const LEADS: Lead[] = SEEDS.map((seed) => {
  const messages = MESSAGES[`c-${seed.key}`] ?? [];
  const score = buildScore(seed);
  const lastInbound = [...messages].reverse().find((m) => m.direction === 'in');
  const windowMsLeft = lastInbound
    ? Math.max(0, 24 * 60 * 60_000 - (NOW - Date.parse(lastInbound.at)))
    : 0;
  const contact: Contact = {
    id: `ct-${seed.key}`,
    displayName: seed.name,
    phone: seed.phone,
    contactHash: `wa_${seed.key.slice(0, 3)}${seed.phone.slice(-4)}`,
    lang: seed.lang,
    city: seed.city,
    customerType: seed.type,
    optedOut: seed.optedOut ?? false,
    createdAt: messages[0]?.at ?? ago(500),
    lastSeenAt: lastInbound?.at ?? ago(500),
    tier: score.tier,
    leadId: `l-${seed.key}`,
  };
  return {
    id: `l-${seed.key}`,
    contact,
    stage: seed.stage,
    stageChangedAt: ago(mins(lastInbound?.at ?? ago(120)) - 5),
    source: seed.type === 'b2b' ? 'referral' : seed.tags.includes('instagram') ? 'ctwa_ad' : 'organic',
    score,
    facts: seed.facts.map(([key, value, source, quote], i) => ({
      key,
      value,
      source,
      at: ago(300 - i * 7),
      superseded: false,
      quote,
    })),
    // Always empty against the real API — see the note on `Seed.tags`.
    tags: [],
    notes:
      seed.key === 'rohit'
        ? [
            {
              id: 'n-rohit-1',
              at: ago(1170),
              author: 'Ritu Bhandari',
              body: 'Called at 4.20pm, no answer. Project sheet in progress — antique brass finish on both room pieces.',
            },
            {
              id: 'n-rohit-2',
              at: ago(295),
              author: 'Ritu Bhandari',
              body: 'Spoke 11am. Wants lobby options at 1.2L and 1.5L. Decision with the owner by Friday.',
            },
          ]
        : seed.key === 'farah'
          ? [
              {
                id: 'n-farah-1',
                at: ago(494),
                author: 'Sneha Pillai',
                body: 'Replacement KL250-RPL-0912 raised. Collection of the damaged piece to be scheduled with dispatch.',
              },
            ]
          : [],
    timeline: timelineFor(seed, messages, score),
    conversationId: `c-${seed.key}`,
    windowMsLeft,
    takeover:
      seed.key === 'rohit'
        ? { by: 'Ritu Bhandari', until: new Date(NOW + 38 * 60_000).toISOString() }
        : seed.key === 'farah'
          ? { by: 'Sneha Pillai', until: new Date(NOW + 9 * 60_000).toISOString() }
          : null,
    openHandoffId: null,
    // `crm.promises` — something we said we would do, and when. Two of the cast are owed one and
    // one of those is late, so the Home queue has a real row in it rather than a permanent
    // "nothing is overdue".
    promises:
      seed.key === 'srinivasan'
        ? [{
            id: 'pr-srinivasan',
            kind: 'callback',
            text: 'Someone from the studio will call you back today about the 80-piece order.',
            language: 'en',
            dueAt: new Date(NOW - 95 * 60_000).toISOString(),
            status: 'open',
            overdue: true,
          }]
        : seed.key === 'anita'
          ? [{
              id: 'pr-anita',
              kind: 'callback',
              text: 'कल सुबह तक डिज़ाइन टीम आपको कॉल करेगी।',
              language: 'hi',
              dueAt: new Date(NOW + 9 * 60 * 60_000).toISOString(),
              status: 'open',
              overdue: false,
            }]
          : [],
  };
});

const leadByKey = new Map(LEADS.map((l) => [l.id.replace(/^l-/, ''), l]));

// ---------------------------------------------------------------------------------------------
// Handoffs — basis and outcome vocabularies from db/migrations/018_handoff.sql
// ---------------------------------------------------------------------------------------------

/**
 * `ageMs` and `claimWaitMs` are not written out six times: they are derived below from
 * `openedAt`/`claimedAt` the same way the API derives them on the server's clock.
 */
const HANDOFF_SEEDS: Omit<Handoff, 'ageMs' | 'claimWaitMs'>[] = [
  {
    id: 'h-anita',
    code: 'H-8F3K2A',
    leadId: 'l-anita',
    conversationId: 'c-anita',
    displayName: 'अनीता शर्मा',
    reasonCode: 'human_request',
    reasonLabel: 'Customer explicitly asked for a human or advisor',
    basis: 'customer_consent',
    quote: 'हाँ, please कॉल करवा दीजिए। मैं अनीता शर्मा, मुंबई से।',
    brief: [
      { label: 'What they want', value: 'Statement side table in marble for a drawing room' },
      { label: 'Piece named', value: 'Cantilever Marble (KL 1483 A) — ₹1,10,000' },
      { label: 'City', value: 'Mumbai' },
      { label: 'Budget', value: null },
      { label: 'Timeline', value: null },
    ],
    status: 'open',
    openedAt: ago(167),
    claimedAt: null,
    claimedBy: null,
    closedAt: null,
    outcome: null,
    outcomeReason: null,
    tier: 'hot',
    score: leadByKey.get('anita')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(31), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'sent', at: ago(31), step: 0, error: null, delivery: 'sent' },
    ],
    escalations: [
      { at: ago(107), kind: 'unclaimed_60m', note: 'Open 60 minutes with no claim — notified #sales' },
      { at: ago(47), kind: 'customer_followed_up', note: 'Customer asked again: "कॉल का कोई अपडेट?"' },
    ],
  },
  {
    id: 'h-siddharth',
    code: 'H-7QM4X1',
    leadId: 'l-siddharth',
    conversationId: 'c-siddharth',
    displayName: 'Siddharth Rao',
    reasonCode: 'human_request',
    reasonLabel: 'Customer explicitly asked for a human or advisor',
    basis: 'customer_consent',
    quote: 'the cheaper one. can someone call me today',
    brief: [
      { label: 'What they want', value: 'Arch Console (KL 2194) — ₹65,000' },
      { label: 'City', value: null },
      { label: 'Budget', value: null },
      { label: 'Home or project', value: null },
    ],
    status: 'open',
    openedAt: ago(79),
    claimedAt: null,
    claimedBy: null,
    closedAt: null,
    outcome: null,
    outcomeReason: null,
    tier: 'warm',
    score: leadByKey.get('siddharth')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(62), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'sent', at: ago(62), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'sent', at: ago(19), step: 1, error: null, delivery: 'sent' },
    ],
    escalations: [{ at: ago(19), kind: 'unclaimed_60m', note: 'Open 60 minutes with no claim — notified #sales' }],
  },
  {
    id: 'h-farah',
    code: 'H-2LP9BC',
    leadId: 'l-farah',
    conversationId: 'c-farah',
    displayName: 'Farah Qureshi',
    reasonCode: 'complaint_or_risk',
    reasonLabel: 'Complaint, urgency, or policy-sensitive request',
    basis: 'policy_required',
    quote: null,
    brief: [
      { label: 'What happened', value: 'Casablanca Mirror Frame Mop (KL 250) arrived with a chipped corner' },
      { label: 'Piece', value: 'KL 250 — ₹49,000' },
      { label: 'City', value: 'Hyderabad' },
      { label: 'Photo supplied', value: 'Yes, 8 minutes after the complaint' },
    ],
    status: 'claimed',
    openedAt: ago(519),
    claimedAt: ago(507),
    claimedBy: 'Sneha Pillai',
    closedAt: null,
    outcome: null,
    outcomeReason: null,
    tier: 'cold',
    score: leadByKey.get('farah')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(12), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'skipped_no_template', at: ago(12), step: 0, error: null, delivery: null },
    ],
    escalations: [],
  },
  {
    id: 'h-rohit',
    code: 'H-5RT8WN',
    leadId: 'l-rohit',
    conversationId: 'c-rohit',
    displayName: 'Rohit Bansal',
    reasonCode: 'project_enquiry',
    reasonLabel: 'B2B project enquiry above the self-serve threshold',
    basis: 'customer_consent',
    quote: 'Yes please. Rohit Bansal, Bansal Hospitality. Best on this number after 4pm.',
    brief: [
      { label: 'Project', value: '40-key boutique hotel, Jaipur, opening March' },
      { label: 'Volume', value: '80 pieces — one console + one mirror per room' },
      { label: 'Pieces proposed', value: 'Arch Console KL 2194 · Bombay Mirror KL 1280' },
      { label: 'Budget', value: null },
    ],
    status: 'in_progress',
    openedAt: ago(1419),
    claimedAt: ago(1390),
    claimedBy: 'Ritu Bhandari',
    closedAt: null,
    outcome: null,
    outcomeReason: null,
    tier: 'hot',
    score: leadByKey.get('rohit')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(8), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'sent', at: ago(8), step: 0, error: null, delivery: 'sent' },
    ],
    escalations: [],
  },
  {
    id: 'h-vikram',
    code: 'H-3DK6YZ',
    leadId: 'l-vikram',
    conversationId: 'c-vikram',
    displayName: 'Vikram Chauhan',
    reasonCode: 'timeline_risk',
    reasonLabel: 'Requested timeline is shorter than the standard dispatch',
    basis: 'system_inferred',
    quote: null,
    brief: [
      { label: 'Project', value: 'Restaurant, 13 pieces, opening in 6 weeks' },
      { label: 'Conflict', value: 'Standard dispatch is 8 weeks' },
      { label: 'City', value: 'Gurugram' },
    ],
    status: 'closed',
    openedAt: ago(2099),
    claimedAt: ago(2080),
    claimedBy: 'Ritu Bhandari',
    closedAt: ago(1395),
    outcome: 'won',
    outcomeReason: 'Phased delivery accepted — phase 1 on 24 Oct, phase 2 on 14 Nov',
    tier: 'hot',
    score: leadByKey.get('vikram')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(140), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: 'routing.reps', status: 'skipped_no_target', at: ago(140), step: 0, error: null, delivery: null },
    ],
    escalations: [],
  },
  {
    id: 'h-srinivasan',
    code: 'H-9WB1TF',
    leadId: 'l-srinivasan',
    conversationId: 'c-srinivasan',
    displayName: 'K. Srinivasan',
    reasonCode: 'project_enquiry',
    reasonLabel: 'B2B project enquiry above the self-serve threshold',
    basis: 'system_inferred',
    quote: null,
    brief: [
      { label: 'Project', value: 'Serviced apartment block, Bengaluru' },
      { label: 'Volume', value: '6 identical side tables' },
      { label: 'Piece proposed', value: 'Brass Cube With Marble Top KL 1310 — ₹29,000' },
    ],
    status: 'closed',
    openedAt: ago(5779),
    claimedAt: ago(5760),
    claimedBy: 'Ritu Bhandari',
    closedAt: ago(5100),
    outcome: 'not_reached',
    outcomeReason: 'Two calls at the agreed 11am slot went unanswered; left the project price on WhatsApp',
    tier: 'warm',
    score: leadByKey.get('srinivasan')?.score.value ?? 0,
    notice: 'New handover H-4K7P2M — discount_request. Reply: take H-4K7P2M\nbasis: system_inferred\nquote: can you do anything on the price\nNot yet known: city\nname: Anita Sharma',
    notifications: [
      { adapter: 'inbox', target: 'inbox', status: 'sent', at: ago(5400), step: 0, error: null, delivery: 'sent' },
      { adapter: 'whatsapp_dm', target: '919928835577', status: 'failed', at: ago(5400), step: 0, error: 'Graph 131047: re-engagement message outside window', delivery: 'failed' },
    ],
    escalations: [{ at: ago(5400), kind: 'no_first_reply_2h', note: 'No human reply 2h after claim' }],
  },
];

export const HANDOFFS: Handoff[] = HANDOFF_SEEDS.map((h) => ({
  ...h,
  ageMs: Math.max(0, NOW - Date.parse(h.openedAt)),
  claimWaitMs: h.claimedAt === null ? null : Math.max(0, Date.parse(h.claimedAt) - Date.parse(h.openedAt)),
}));

for (const h of HANDOFFS) {
  if (h.status === 'open' || h.status === 'claimed' || h.status === 'in_progress') {
    const lead = LEADS.find((l) => l.id === h.leadId);
    if (lead) lead.openHandoffId = h.id;
  }
}

// ---------------------------------------------------------------------------------------------
// Conversations (the Inbox list)
// ---------------------------------------------------------------------------------------------

export const CONVERSATIONS: Conversation[] = LEADS.map((lead): Conversation => {
  const messages = MESSAGES[lead.conversationId] ?? [];
  const last = messages[messages.length - 1];
  const lastInbound = [...messages].reverse().find((m) => m.direction === 'in');
  const lastOutbound = [...messages].reverse().find((m) => m.direction === 'out');
  const unanswered = last?.direction === 'in';
  const handoff = HANDOFFS.find((h) => h.leadId === lead.id && h.status !== 'closed');
  return {
    conversationId: lead.conversationId,
    leadId: lead.id,
    contactHash: lead.contact.contactHash,
    displayName: lead.contact.displayName,
    phone: lead.contact.phone,
    lastMessagePreview: last?.body ?? '',
    lastInboundAt: lastInbound?.at ?? ago(9999),
    lastOutboundAt: lastOutbound?.at ?? null,
    unanswered,
    unansweredMs: unanswered && lastInbound ? NOW - Date.parse(lastInbound.at) : 0,
    tier: lead.score.tier,
    stage: lead.stage,
    lang: lead.contact.lang,
    handoff: handoff ? { id: handoff.id, status: handoff.status, claimedBy: handoff.claimedBy } : null,
    takeover: lead.takeover,
  };
}).sort((a, b) => Date.parse(b.lastInboundAt) - Date.parse(a.lastInboundAt));

// ---------------------------------------------------------------------------------------------
// Explain — what the reasoner did on one turn (ops/manager only)
// ---------------------------------------------------------------------------------------------

export const EXPLAINS: Record<string, Explain> = {
  't-anita-3': {
    turnId: 't-anita-3',
    directive:
      'Customer named marble and a statement look for a drawing room. Show at most two pieces that match both, lead with the marble-only piece, and keep the funnel moving by asking nothing this turn — they have given four of five slots.',
    retrieved: [
      { productId: 'mc-cantilever-marble', title: 'Cantilever Marble (KL 1483 A)', score: 0.91, why: 'material=Marble, style=statement, category=side table' },
      { productId: 'mc-agate-top-side-table', title: 'Agate Top Cocktail Table (KL 2105)', score: 0.78, why: 'material=Agate Stone/Marble Base, price below stated ceiling' },
      { productId: 'mc-amber-marble-side-table', title: 'Amber Marble Cocktail Table (KL 1821 B)', score: 0.64, why: 'material=Marble — dropped, near-duplicate of KL 1483 A' },
    ],
    verdicts: [
      { guard: 'price_accuracy', verdict: 'pass', note: 'Both prices match catalogue rows exactly' },
      { guard: 'no_invented_products', verdict: 'pass', note: '2 of 2 products resolve to catalogue ids' },
      { guard: 'language_match', verdict: 'rewrite', note: 'First draft mixed English clauses into a Hindi reply; rewritten in Devanagari with product names kept in Latin' },
      { guard: 'media_allowed', verdict: 'pass', note: 'Both hero images carry allowed_send=true' },
    ],
    stageLog: [
      { stage: 'extract', ms: 412, note: '3 facts written, 0 superseded' },
      { stage: 'retrieve', ms: 96, note: '3 candidates, 2 kept' },
      { stage: 'direct', ms: 1180, note: 'Directive produced' },
      { stage: 'compose', ms: 1904, note: 'Draft 1 rejected by language_match, draft 2 accepted' },
      { stage: 'guard', ms: 240, note: '4 guards, 1 rewrite' },
      { stage: 'send', ms: 620, note: '1 text + 2 images enqueued' },
    ],
    tokens: { in: 3820, out: 412 },
    costInr: 2.14,
    model: 'claude-sonnet-4-5',
  },
  't-vikram-1': {
    turnId: 't-vikram-1',
    directive:
      'A 6-week opening against an 8-week dispatch is a promise we cannot make. Do not quote a date. Hand over and say plainly that a human will set the timeline.',
    retrieved: [],
    verdicts: [
      { guard: 'no_delivery_promise', verdict: 'block', note: 'Draft 1 said "we can manage 6 weeks" — blocked, no ledger row supports it' },
      { guard: 'handoff_required', verdict: 'pass', note: 'timeline_risk trigger fired; handoff opened with basis=system_inferred' },
    ],
    stageLog: [
      { stage: 'extract', ms: 380, note: '4 facts written' },
      { stage: 'direct', ms: 990, note: 'Directive produced' },
      { stage: 'compose', ms: 2210, note: 'Draft 1 blocked, draft 2 accepted' },
      { stage: 'guard', ms: 190, note: '2 guards, 1 block' },
    ],
    tokens: { in: 2960, out: 188 },
    costInr: 1.62,
    model: 'claude-sonnet-4-5',
  },
};

/** Any other turn gets a generic-but-honest record rather than a fabricated one. */
export function explainFor(turnId: string): Explain {
  const known = EXPLAINS[turnId];
  if (known) return known;
  return {
    turnId,
    directive: 'Keep the funnel moving: ask for the next unknown slot, answer what was asked, never invent a price.',
    retrieved: [],
    verdicts: [{ guard: 'price_accuracy', verdict: 'pass', note: 'No price asserted in this turn' }],
    stageLog: [
      { stage: 'extract', ms: 350, note: 'facts unchanged' },
      { stage: 'direct', ms: 870, note: 'Directive produced' },
      { stage: 'compose', ms: 1490, note: 'Draft accepted first pass' },
      { stage: 'guard', ms: 150, note: '1 guard, 0 fires' },
    ],
    tokens: { in: 2410, out: 142 },
    costInr: 1.18,
    model: 'claude-sonnet-4-5',
  };
}

// ---------------------------------------------------------------------------------------------
// Pipeline — the pack has no `funnel.stages` yet (task B3 adds it); this is the working vocabulary
// ---------------------------------------------------------------------------------------------

/**
 * Exactly the API's six stages — `STAGE_LABELS` in `packages/core/src/api/view.ts`, which is 017's
 * CHECK. This list had seven (`quoted`, `won`, `dormant` and no `ready`/`closed`), so the demo showed
 * a pipeline the real product can never produce: a mock that is kinder than the API is how a screen
 * gets built for data that does not exist.
 */
export const STAGE_LABELS: { key: string; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'engaged', label: 'Engaged' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'ready', label: 'Ready' },
  { key: 'handed_over', label: 'Handed over' },
  { key: 'closed', label: 'Closed' },
];

export function pipelineStages(cards: PipelineCard[]): PipelineStage[] {
  return STAGE_LABELS.map((s) => ({ ...s, count: cards.filter((c) => c.stage === s.key).length }));
}

export const PIPELINE_CARDS: PipelineCard[] = LEADS.map((lead) => {
  const interest = lead.facts.find((f) => f.key === 'product_interest')?.value;
  const scope = lead.facts.find((f) => f.key === 'project_scope')?.value;
  return {
    leadId: lead.id,
    stage: lead.stage,
    displayName: lead.contact.displayName,
    tier: lead.score.tier,
    score: lead.score.value,
    lastInboundAt: lead.contact.lastSeenAt,
    headline: scope ?? interest ?? lead.facts.find((f) => f.key === 'category')?.value ?? 'No requirement captured yet',
    // `routes/pipeline.ts` hard-codes null: nothing in this product collects a deal value, and a
    // demo board totalling ₹41 lakh of "named interest" promised a column the real one cannot fill.
    valueInr: null,
  };
});

// ---------------------------------------------------------------------------------------------
// Metrics — every number the console shows carries its definition (Global Constraint)
// ---------------------------------------------------------------------------------------------

export const METRIC_DEFINITIONS: MetricDefinition[] = [
  {
    name: 'conversations_today',
    label: 'Conversations today',
    sql: "SELECT count(DISTINCT conversation_id) FROM convo.messages WHERE tenant_id = $1 AND event_time >= date_trunc('day', now() AT TIME ZONE $2)",
    window: 'today, tenant timezone',
    owner: 'ops',
    unit: 'count',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'new_leads_today',
    label: 'New leads today',
    sql: "SELECT count(*) FROM crm.leads WHERE tenant_id = $1 AND created_at >= date_trunc('day', now() AT TIME ZONE $2)",
    window: 'today, tenant timezone',
    owner: 'sales',
    unit: 'count',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'unanswered_over_5m',
    label: 'Unanswered over 5 min',
    sql: "SELECT count(*) FROM convo.conversations c WHERE c.tenant_id = $1 AND c.last_inbound_at > coalesce(c.last_outbound_at, '-infinity') AND now() - c.last_inbound_at > interval '5 minutes'",
    window: 'live',
    owner: 'ops',
    unit: 'count',
    temporal: 'current_state',
    shape: 'scalar',
  },
  {
    name: 'first_reply_p50',
    label: 'First reply p50',
    sql: "SELECT percentile_disc(0.5) WITHIN GROUP (ORDER BY reply_ms) FROM ops.first_reply WHERE tenant_id = $1 AND at >= now() - interval '24 hours'",
    window: 'rolling 24 h',
    owner: 'ops',
    unit: 'ms',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'first_reply_p90',
    label: 'First reply p90',
    sql: "SELECT percentile_disc(0.9) WITHIN GROUP (ORDER BY reply_ms) FROM ops.first_reply WHERE tenant_id = $1 AND at >= now() - interval '24 hours'",
    window: 'rolling 24 h',
    owner: 'ops',
    unit: 'ms',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'handoffs_open',
    label: 'Handoffs open',
    sql: "SELECT count(*) FROM handoff.handoffs WHERE tenant_id = $1 AND status IN ('open','claimed','in_progress')",
    window: 'live',
    owner: 'sales',
    unit: 'count',
    temporal: 'current_state',
    shape: 'scalar',
  },
  {
    name: 'handoff_claim_p90',
    label: 'Claim time p90',
    sql: "SELECT percentile_disc(0.9) WITHIN GROUP (ORDER BY extract(epoch FROM claimed_at - opened_at) * 1000) FROM handoff.handoffs WHERE tenant_id = $1 AND claimed_at >= now() - interval '7 days'",
    window: 'rolling 7 d',
    owner: 'sales',
    unit: 'ms',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'outcome_recorded_rate',
    label: 'Outcome recorded',
    sql: "SELECT count(*) FILTER (WHERE outcome IS NOT NULL)::numeric / nullif(count(*), 0) * 100 FROM handoff.handoffs WHERE tenant_id = $1 AND status = 'closed' AND closed_at >= now() - interval '30 days'",
    window: 'rolling 30 d',
    owner: 'sales',
    unit: 'percent',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'messages_in_today',
    label: 'Messages in',
    sql: "SELECT count(*) FROM convo.messages WHERE tenant_id = $1 AND direction = 'in' AND event_time >= date_trunc('day', now() AT TIME ZONE $2)",
    window: 'today, tenant timezone',
    owner: 'ops',
    unit: 'count',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'messages_out_today',
    label: 'Messages out',
    sql: "SELECT count(*) FROM convo.messages WHERE tenant_id = $1 AND direction = 'out' AND event_time >= date_trunc('day', now() AT TIME ZONE $2)",
    window: 'today, tenant timezone',
    owner: 'ops',
    unit: 'count',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'guard_fire_rate',
    label: 'Guard fire rate',
    sql: "SELECT count(*) FILTER (WHERE verdict <> 'pass')::numeric / nullif(count(*), 0) * 100 FROM ops.guard_verdicts WHERE tenant_id = $1 AND at >= now() - interval '7 days'",
    window: 'rolling 7 d',
    owner: 'ops',
    unit: 'percent',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'cost_inr_today',
    label: 'Model cost today',
    sql: "SELECT coalesce(sum(cost_inr), 0) FROM convo.turns WHERE tenant_id = $1 AND started_at >= date_trunc('day', now() AT TIME ZONE $2)",
    window: 'today, tenant timezone',
    owner: 'ops',
    unit: 'inr',
    temporal: 'point_in_time',
    shape: 'scalar',
  },
  {
    name: 'hot_leads',
    label: 'Hot leads',
    sql: 'SELECT count(*) FROM crm.leads WHERE tenant_id = $1 AND closed_at IS NULL AND score >= 70',
    window: 'live',
    owner: 'sales',
    unit: 'count',
    temporal: 'current_state',
    shape: 'scalar',
  },
];

const definitionByName = new Map(METRIC_DEFINITIONS.map((d) => [d.name, d]));

const METRIC_VALUES: Record<string, { value: number; delta: number | null }> = {
  conversations_today: { value: 9, delta: 2 },
  new_leads_today: { value: 4, delta: -1 },
  unanswered_over_5m: { value: CONVERSATIONS.filter((c) => c.unanswered && c.unansweredMs > 5 * 60_000).length, delta: 1 },
  first_reply_p50: { value: 47_000, delta: -6_000 },
  first_reply_p90: { value: 104_000, delta: 11_000 },
  handoffs_open: { value: HANDOFFS.filter((h) => h.status !== 'closed').length, delta: 1 },
  handoff_claim_p90: { value: 62 * 60_000, delta: 9 * 60_000 },
  outcome_recorded_rate: { value: 100, delta: 0 },
  messages_in_today: { value: 31, delta: 5 },
  messages_out_today: { value: 28, delta: 4 },
  guard_fire_rate: { value: 6.4, delta: -0.8 },
  cost_inr_today: { value: 34.7, delta: 5.2 },
  hot_leads: { value: LEADS.filter((l) => l.score.tier === 'hot').length, delta: 1 },
};

export function metricValues(names?: string[]): MetricValue[] {
  const wanted = names?.length ? names : METRIC_DEFINITIONS.map((d) => d.name);
  return wanted.flatMap((name) => {
    const definition = definitionByName.get(name);
    const v = METRIC_VALUES[name];
    if (!definition || !v) return [];
    // `rows` and `unavailable` are on the wire for every metric: a scalar carries `rows: null`,
    // and `unavailable` holds the sentence the API prints INSTEAD of a figure when there is none.
    // The demo's numbers are all present, so both are null here.
    return [{ name, value: v.value, delta: v.delta, rows: null, unavailable: null, definition }];
  });
}

// ---------------------------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------------------------

export const HEALTH: HealthSummary = {
  services: [
    { key: 'db', label: 'Postgres', state: 'ok', detail: 'primary, 3 ms, 11 connections' },
    { key: 'queue', label: 'Job queue', state: 'ok', detail: 'depth 2, oldest job 14 s' },
    { key: 'reasoner', label: 'Reasoner circuit', state: 'ok', detail: 'closed, 0 trips in 24 h' },
    { key: 'meta_token', label: 'Meta token', state: 'warn', detail: 'system user token is 51 days old — rotate before 60' },
    { key: 'display_name', label: 'WABA display name', state: 'warn', detail: 'production number display name still PENDING at Meta' },
    { key: 'telegram', label: 'Telegram handoff adapter', state: 'down', detail: 'H1_MAIN_CHARACTER_TELEGRAM_BOT_TOKEN not set — handoffs are inbox-only' },
  ],
  unanswered: {
    count: CONVERSATIONS.filter((c) => c.unanswered).length,
    maxAgeMs: Math.max(0, ...CONVERSATIONS.map((c) => c.unansweredMs)),
  },
  deliveryFailures: [
    { class: '131047 — re-engagement outside 24 h window', count: 3 },
    { class: '131026 — recipient cannot receive', count: 1 },
    { class: '132000 — template param mismatch', count: 0 },
  ],
  guardFires: [
    { guard: 'price_accuracy', fires7d: 4 },
    { guard: 'no_invented_products', fires7d: 2 },
    { guard: 'language_match', fires7d: 17 },
    { guard: 'no_delivery_promise', fires7d: 6 },
    { guard: 'media_allowed', fires7d: 0 },
  ],
  outbox: [
    { state: 'sent', count: 41 },
    { state: 'pending', count: 2 },
    { state: 'failed', count: 1 },
  ],
  queue: { depth: 2, oldestMs: 14_000, detail: 'pg-boss, 2 jobs waiting' },
  packDigest: 'sha256:4f1c9e7a…21b0',
  configDrift: false,
  lastBackupAgeMs: null,
};

export { MOCK_PRODUCTS };
